import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, House, Users, Sparkle } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import MarketingLayout from "@/components/MarketingLayout";
import SEO from "@/components/SEO";
import { toast } from "sonner";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/billing/plans").then(({ data }) => setPlans(data.plans)); }, []);

  const subscribe = async (planId) => {
    if (!user || user === false) {
      navigate(`/register`);
      return;
    }
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatError(e));
      setBusy(null);
    }
  };

  const freeFeatures = ["25 items", "5 locations", "Unlimited categories", "Search"];

  const content = (
    <div className="space-y-10" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-terracotta-600">
          <Sparkle weight="fill" size={14} /> Plans
        </span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl text-stoneink-900 font-semibold tracking-tight">
          Simple, honest pricing.
        </h1>
        <p className="mt-4 text-stoneink-700">Start free, upgrade when your home grows.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Free */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-sand-300 bg-white p-8 shadow-ambient flex flex-col"
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-sand-200 text-stoneink-700 grid place-items-center">
              <House weight="duotone" size={22} />
            </div>
            <h3 className="font-heading text-2xl font-medium text-stoneink-900">Free</h3>
          </div>
          <div className="mt-6">
            <span className="font-heading text-5xl font-semibold text-stoneink-900">$0</span>
            <span className="text-stoneink-500 ml-1">/forever</span>
          </div>
          <ul className="mt-6 space-y-3 text-stoneink-700 flex-1">
            {freeFeatures.map((f) => (
              <li key={f} className="flex items-center gap-2"><Check weight="bold" className="text-sage-600" size={18} /> {f}</li>
            ))}
          </ul>
          <button disabled className="mt-8 h-12 w-full rounded-xl border border-sand-300 bg-sand-100 text-stoneink-500" data-testid="plan-free-btn">
            {user?.tier === "free" ? "Current plan" : "Free"}
          </button>
        </motion.div>

        {plans.map((p, i) => (
          <motion.div key={p.id}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.05 }}
            className={`rounded-3xl p-8 shadow-ambient flex flex-col border ${p.id === "pro" ? "bg-stoneink-900 text-white border-stoneink-900 relative" : "bg-white border-sand-300"}`}
            data-testid={`plan-${p.id}`}
          >
            {p.id === "pro" && (
              <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-terracotta-500 text-white text-xs font-medium">
                Most popular
              </span>
            )}
            <div className="flex items-center gap-3">
              <div className={`h-11 w-11 rounded-2xl grid place-items-center ${p.id === "pro" ? "bg-terracotta-500 text-white" : "bg-ochre-400/30 text-ochre-600"}`}>
                {p.id === "family" ? <Users weight="duotone" size={22} /> : <Sparkle weight="duotone" size={22} />}
              </div>
              <h3 className={`font-heading text-2xl font-medium ${p.id === "pro" ? "text-white" : "text-stoneink-900"}`}>{p.name}</h3>
            </div>
            <div className="mt-6">
              <span className={`font-heading text-5xl font-semibold ${p.id === "pro" ? "text-white" : "text-stoneink-900"}`}>${p.amount}</span>
              <span className={`ml-1 ${p.id === "pro" ? "text-white/70" : "text-stoneink-500"}`}>/month</span>
            </div>
            <ul className={`mt-6 space-y-3 flex-1 ${p.id === "pro" ? "text-white/85" : "text-stoneink-700"}`}>
              {p.features.map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <Check weight="bold" className={p.id === "pro" ? "text-terracotta-400" : "text-sage-600"} size={18} /> {f}
                </li>
              ))}
            </ul>
            <button
              data-testid={`subscribe-${p.id}-btn`}
              onClick={() => subscribe(p.id)} disabled={busy === p.id || user?.tier === p.id}
              className={`mt-8 h-12 w-full rounded-xl font-medium transition-all active:scale-[0.98] disabled:opacity-60 ${
                p.id === "pro"
                  ? "bg-terracotta-500 hover:bg-terracotta-600 text-white"
                  : "bg-stoneink-900 hover:bg-stoneink-700 text-white"
              }`}
            >
              {user?.tier === p.id ? "Current plan" : busy === p.id ? "Redirecting…" : `Get ${p.name}`}
            </button>
          </motion.div>
        ))}
      </div>

      <div className="text-center text-sm text-stoneink-500">
        Cancel anytime. No commitments.
      </div>
    </div>
  );

  // Logged-in users: render inside the app layout with sidebar
  if (user) return <AppLayout>{content}</AppLayout>;

  // Public visitor: marketing layout
  return (
    <MarketingLayout>
      <SEO
        title="Pricing"
        description="Free, Pro $4.99/mo, and Family $9.99/mo (5 members). Honest pricing for a cozy home-inventory app. Cancel anytime."
        path="/pricing"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Nestled",
          "description": "Cozy home-inventory app: photos, voice quick-add, nested locations, QR labels, family sharing.",
          "brand": { "@type": "Brand", "name": "Nestled" },
          "offers": [
            { "@type": "Offer", "name": "Free", "price": "0", "priceCurrency": "USD", "url": "https://nestledapp.io/pricing" },
            { "@type": "Offer", "name": "Pro", "price": "4.99", "priceCurrency": "USD", "url": "https://nestledapp.io/pricing", "priceSpecification": { "@type": "RecurringPaymentsPriceSpecification", "billingDuration": "P1M" } },
            { "@type": "Offer", "name": "Family", "price": "9.99", "priceCurrency": "USD", "url": "https://nestledapp.io/pricing", "priceSpecification": { "@type": "RecurringPaymentsPriceSpecification", "billingDuration": "P1M" } },
          ],
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">{content}</div>
    </MarketingLayout>
  );
}
